import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Bookmarks = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/bookmarks`);
      setBookmarks(response.data);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  const deleteBookmark = async (verseKey) => {
    try {
      await axios.delete(`${API}/bookmarks/${verseKey}`);
      toast.success('Bookmark removed');
      fetchBookmarks();
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      toast.error('Failed to delete bookmark');
    }
  };

  const goToVerse = (chapterId) => {
    navigate(`/surah/${chapterId}`);
  };

  return (
    <div className="min-h-screen pb-32">
      {/* Header */}
      <div className="bg-white border-b border-[#E8EAE8]">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#3A5A40] hover:text-[#2B4330] mb-6"
            data-testid="back-button"
          >
            <ArrowLeft size={20} strokeWidth={1.5} />
            <span className="font-medium">Back to Surahs</span>
          </button>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-light tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="bookmarks-title"
          >
            Bookmarks
          </motion.h1>
        </div>
      </div>

      {/* Bookmarks List */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-12 text-[#6B726B]">Loading...</div>
        ) : bookmarks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 text-[#6B726B]"
            data-testid="no-bookmarks"
          >
            No bookmarks yet. Start reading and bookmark your favorite verses!
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="bookmarks-list">
            {bookmarks.map((bookmark, index) => (
              <motion.div
                key={bookmark.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className="bg-white border border-[#E8EAE8] rounded-2xl p-6 hover:border-[#A3B18A] transition-all duration-300"
                data-testid={`bookmark-${bookmark.verse_key}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 cursor-pointer" onClick={() => goToVerse(bookmark.chapter_id)}>
                    <div className="text-sm text-[#A3B18A] font-medium mb-2" style={{ letterSpacing: '0.2em' }}>
                      SURAH {bookmark.chapter_id} · AYAH {bookmark.verse_number}
                    </div>
                    <div className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {bookmark.verse_key}
                    </div>
                    <div className="text-sm text-[#6B726B] mt-1">
                      {new Date(bookmark.timestamp).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteBookmark(bookmark.verse_key)}
                    className="text-[#6B726B] hover:text-red-500 transition-colors"
                    data-testid={`delete-bookmark-${bookmark.verse_key}`}
                  >
                    <Trash2 size={18} strokeWidth={1.5} />
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Bookmarks;